import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { JobBoard } from "@/components/JobBoard";
import { AddJobDialog } from "@/components/AddJobDialog";
import { FeedbackDialog } from "@/components/FeedbackDialog";
import FocusMode from "@/components/FocusMode";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PlusCircle, Link as LinkIcon, MessageSquarePlus, Moon, Sun, Palette, CalendarDays } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";

// Demo data to be used instead of Supabase
const initialDemoJobs = [
  {
    id: 1,
    company_name: "Tech Innovators Inc.",
    job_title: "Senior Frontend Developer",
    description: "Looking for an experienced frontend developer to join our dynamic team.",
    status: "Applied",
    date_added: "2024-01-15",
    url: "https://example.com/job1"
  },
  {
    id: 2,
    company_name: "Digital Solutions Co.",
    job_title: "Full Stack Engineer",
    description: "Join our growing team to build scalable web applications.",
    status: "Interview",
    date_added: "2024-01-18",
    url: "https://example.com/job2"
  },
  {
    id: 3,
    company_name: "StartupX",
    job_title: "React Developer",
    description: "Help us build the next generation of web applications.",
    status: "Interested",
    date_added: "2024-01-20",
    url: "https://example.com/job3"
  },
  {
    id: 4,
    company_name: "Global Tech Ltd.",
    job_title: "Senior Software Engineer",
    description: "Looking for a passionate developer to lead our frontend initiatives.",
    status: "Offer",
    date_added: "2024-01-22",
    url: "https://example.com/job4"
  }
];

const DemoBoard = () => {
  const [open, setOpen] = useState(false);
  const [quickAddUrl, setQuickAddUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [bgColor, setBgColor] = useState('default');
  const [localDemoJobs, setLocalDemoJobs] = useState(initialDemoJobs);
  const jobBoardRef = useRef<{ fetchJobs: () => void }>();
  const { toast } = useToast();
  const [showFocusMode, setShowFocusMode] = useState(false);

  const backgroundColors = {
    default: 'bg-[#2A2B2A]',
    blue: 'bg-[#2A2B2A]',
    green: 'bg-[#2A2B2A]',
    purple: 'bg-[#2A2B2A]',
    rose: 'bg-[#2A2B2A]',
    amber: 'bg-[#2A2B2A]',
    teal: 'bg-[#2A2B2A]',
  };

  useEffect(() => {
    localStorage.setItem('jobpin-dark-mode', 'true');
    localStorage.setItem('jobpin-bg-color', bgColor);
  }, [bgColor]);

  const toggleDarkMode = () => {
    toast({
      title: "Demo Mode is always in Dark Mode",
      duration: 1500,
    });
  };

  const cycleBackgroundColor = () => {
    const colors = Object.keys(backgroundColors);
    const currentIndex = colors.indexOf(bgColor);
    const nextIndex = (currentIndex + 1) % colors.length;
    const nextColor = colors[nextIndex];
    setBgColor(nextColor);
    
    toast({
      title: "Background Updated",
      duration: 1500,
    });
  };

  const handleAddJob = (newJob: any) => {
    const jobToAdd = {
      ...newJob,
      id: Date.now(), // Use timestamp as unique ID
      date_added: new Date().toISOString().split('T')[0],
      status: newJob.status || "Interested"
    };
    
    setLocalDemoJobs(prev => [jobToAdd, ...prev]);
    toast({
      title: "Job Added",
      description: "New job has been added to your demo board.",
    });
  };

  const handleQuickAdd = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && quickAddUrl.trim()) {
      setIsLoading(true);
      
      try {
        // Extract domain as company name
        const url = new URL(quickAddUrl);
        const domain = url.hostname.replace('www.', '').split('.')[0];
        const companyName = domain.charAt(0).toUpperCase() + domain.slice(1);
        
        // Create a demo job from the URL
        const newJob = {
          id: Date.now(),
          company_name: companyName,
          job_title: "New Position from " + companyName,
          description: `Job listing URL: ${quickAddUrl}\n\nThis is a quick-added job. Click "Edit" to update the details.`,
          status: "Interested",
          date_added: new Date().toISOString().split('T')[0],
          url: quickAddUrl
        };

        // Update localDemoJobs using handleAddJob to ensure consistency
        handleAddJob(newJob);
        setQuickAddUrl("");
      } catch (error) {
        toast({
          title: "Error adding job",
          description: "Please enter a valid URL.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleDeleteJob = (jobId: string | number) => {
    setLocalDemoJobs(prev => prev.filter(job => job.id !== jobId));
    toast({
      title: "Job Deleted",
      description: "Job has been removed from your demo board.",
    });
  };

  const handleUpdateJob = (updatedJob: any) => {
    setLocalDemoJobs(prev => 
      prev.map(job => job.id === updatedJob.id ? updatedJob : job)
    );
    toast({
      title: "Job Updated",
      description: "Job has been updated on your demo board.",
    });
  };

  const toggleFocusMode = () => {
    setShowFocusMode(!showFocusMode);
    console.log('DemoBoard - Toggling Focus Mode with demoMode=true');
  };

  return (
    <div className={cn(
      "min-h-screen transition-colors duration-300",
      backgroundColors[bgColor as keyof typeof backgroundColors],
      isDarkMode ? 'text-white' : 'text-gray-900'
    )}>
      {/* Header Section */}
      <header className={cn(
        "fixed top-0 left-0 right-0 z-50 border-b shadow-sm",
        isDarkMode ? "bg-[#333433]/50 backdrop-blur-sm border-[#404140]" : "bg-white border-gray-200"
      )}>
        <div className="max-w-7xl mx-auto p-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="hover:opacity-90 transition-opacity">
              <img 
                src={isDarkMode ? "/JobPin_logo_2edt_white.svg" : "/JobPin_logo_2edt.svg"}
                alt="JobPin Logo" 
                className="h-12 w-auto"
              />
            </Link>
            <div className="flex-1 max-w-xl mx-auto px-8">
              <div className="relative">
                <LinkIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Paste job URL and press Enter to quick-add... (Demo Mode)"
                  value={quickAddUrl}
                  onChange={(e) => setQuickAddUrl(e.target.value)}
                  onKeyDown={handleQuickAdd}
                  className={cn(
                    "pl-9 w-full",
                    isDarkMode ? "bg-[#404140] border-[#4A4B4A]" : "bg-white",
                    isLoading && "opacity-50 cursor-wait"
                  )}
                  disabled={isLoading}
                />
                {isLoading && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400" />
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={toggleFocusMode}
                className={cn(
                  "min-w-[120px]",
                  isDarkMode 
                    ? "bg-[#818881]/20 text-[#D9D9D9] hover:bg-[#818881]/30 border-[#818881]" 
                    : "text-gray-600 hover:text-gray-900"
                )}
              >
                <CalendarDays className="h-4 w-4 mr-2" />
                Focus Mode
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsFeedbackOpen(true)}
                className={cn(
                  "min-w-[100px]",
                  isDarkMode 
                    ? "bg-[#818881]/20 text-[#D9D9D9] hover:bg-[#818881]/30 border-[#818881]" 
                    : "text-gray-600 hover:text-gray-900"
                )}
              >
                <MessageSquarePlus className="h-4 w-4 mr-2" />
                Feedback
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24">
        <div className="max-w-7xl mx-auto p-4">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-semibold">Demo Job Board</h1>
            <div className="flex items-center gap-2">
              <Button 
                onClick={toggleFocusMode}
                variant="outline"
                className="min-w-[120px] bg-[#818881]/20 text-[#D9D9D9] hover:bg-[#CCF751]/90 hover:text-[#2C2E2D] hover:border-[#CCF751]"
              >
                <CalendarDays className="h-4 w-4 mr-2" />
                Focus Mode
              </Button>
              <Button 
                onClick={() => setOpen(true)}
                className="bg-[#CCF751] text-[#2C2E2D] hover:bg-[#CCF751]/90"
              >
                <PlusCircle className="h-4 w-4 mr-2" />
                Add Job
              </Button>
            </div>
          </div>
          
          {showFocusMode ? (
            <FocusMode isDarkMode={isDarkMode} demoMode={true} />
          ) : (
            <JobBoard 
              ref={jobBoardRef} 
              demoMode 
              demoJobs={localDemoJobs}
              isDarkMode={isDarkMode}
              onDeleteJob={handleDeleteJob}
              onUpdateJob={handleUpdateJob}
            />
          )}
        </div>
      </main>

      <AddJobDialog 
        open={open} 
        onOpenChange={setOpen}
        demoMode
        onJobAdded={handleAddJob}
        isDarkMode={isDarkMode}
      />

      <FeedbackDialog
        open={isFeedbackOpen}
        onOpenChange={setIsFeedbackOpen}
        isDarkMode={isDarkMode}
      />
    </div>
  );
};

export default DemoBoard; 